import { message } from "antd";
import type { ColDef, Column, GridApi } from "ag-grid-enterprise";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { ProjectPermissionEnum } from "../../constants";
import { OPEN_CONDITIONAL_FORMATTING_EVENT } from "./conditional-formatting-header-menu";
import { normalizeConditionalFormattingRules } from "./normalize-rules";
import SetupConditionalFormattingPopup, {
  ConditionalFormattingColumnOption
} from "./setup-conditional-formatting-popup";
import type {
  ConditionalFormattingColumnConfig,
  ConditionalFormattingContext,
  ConditionalFormattingFilterType,
  ConditionalFormattingRule,
  GroupBySelectorProps
} from "./types";

const EMPTY_FORMATTING: ConditionalFormattingRule[] = [];

const toStringOptions = (values: unknown): string[] => {
  if (!Array.isArray(values)) return [];
  return Array.from(
    new Set(
      values
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter(
          (value) => value !== null && value !== undefined && value !== ""
        )
        .map(String)
    )
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
};

const resolveFilterName = (filter: ColDef["filter"]): string => {
  if (!filter) return "";
  if (typeof filter === "string") return filter;
  if (typeof filter === "function") return filter.name || "";
  if (typeof filter === "object") {
    const value = filter as {
      name?: string;
      component?: { name?: string; displayName?: string };
    };
    return (
      value.name || value.component?.name || value.component?.displayName || ""
    );
  }
  return "";
};

const inferFilterType = (
  colDef: ColDef,
  config?: ConditionalFormattingColumnConfig
): ConditionalFormattingFilterType => {
  if (config?.filterType) return config.filterType;
  const dataType = String(
    colDef.cellDataType || (colDef as { dataType?: string }).dataType || ""
  ).toLowerCase();
  const filterName = resolveFilterName(colDef.filter).toLowerCase();
  if (filterName.includes("date") || dataType.includes("date")) return "date";
  if (filterName.includes("set")) return "set";
  if (filterName.includes("number") || dataType === "number") return "number";

  const editorName =
    typeof colDef.cellEditor === "string"
      ? colDef.cellEditor.toLowerCase()
      : String(
          (colDef.cellEditor as { name?: string })?.name || ""
        ).toLowerCase();
  return editorName.includes("select") || editorName.includes("dropdown")
    ? "set"
    : "text";
};

const getConfiguredOptions = (
  colDef: ColDef,
  config?: ConditionalFormattingColumnConfig
): string[] => {
  if (config?.options) return toStringOptions(config.options);
  const editorParams =
    typeof colDef.cellEditorParams === "object" && colDef.cellEditorParams
      ? colDef.cellEditorParams
      : undefined;
  return toStringOptions(
    (editorParams as { values?: unknown } | undefined)?.values
  );
};

const EXCLUDED_COLUMN_IDS = new Set([
  "ag-Grid-SelectionColumn",
  "bidding_subcontractors",
  "linked_design_packages",
  "linked_tasks",
  "linked_materials",
  "linked_submittals",
  "linked_activities",
  "locations",
  // Hierarchy group-only leaf counts — not a meaningful per-row CF target.
  "scopeItemCount"
]);

const isExcludedColumn = (columnId: string) =>
  EXCLUDED_COLUMN_IDS.has(columnId);

type OpenConditionalFormattingDetail = {
  columnId?: string;
  api?: GridApi;
};

export default function ConditionalFormattingToolPanel<TData = unknown>({
  api,
  gridRef,
  context,
  rules,
  initialRules = EMPTY_FORMATTING,
  onRulesChange,
  columnConfig
}: GroupBySelectorProps<TData>) {
  const { tokenContents } = useContext(ProjectContext);
  const cannotEditConditionalFormatting = isPermissionNotGrantted(
    ProjectPermissionEnum.EditConditionalFormatting,
    tokenContents?.role ?? ""
  );
  const liveApiRef = useRef<GridApi<TData> | undefined>(api);
  liveApiRef.current = api ?? gridRef?.current?.api;

  const getLiveContext = () =>
    liveApiRef.current?.getGridOption?.("context") as
      | (ConditionalFormattingContext & Record<string, unknown>)
      | undefined;
  const readRules = () =>
    normalizeConditionalFormattingRules(
      rules ??
        getLiveContext()?.conditionalFormattingRules ??
        context?.conditionalFormattingRules ??
        initialRules
    );

  const [formattingColumns, setFormattingColumns] =
    useState<ConditionalFormattingRule[]>(readRules);
  const [gridColumns, setGridColumns] = useState<Column[]>(
    () => liveApiRef.current?.getAllGridColumns?.() ?? []
  );
  const [liveColumnConfig, setLiveColumnConfig] = useState(
    () => getLiveContext()?.conditionalFormattingColumnConfig
  );
  const [popup, setPopup] = useState<{
    columnId?: string;
    value?: ConditionalFormattingRule;
  } | null>(null);

  const mergedColumnConfig =
    columnConfig ??
    liveColumnConfig ??
    context?.conditionalFormattingColumnConfig;

  const columns = useMemo<ConditionalFormattingColumnOption[]>(
    () =>
      gridColumns
        .filter((column) => {
          const columnId = column.getColId();
          return !isExcludedColumn(columnId);
        })
        .map((column) => {
          const colDef = column.getColDef();
          const id = column.getColId();
          const config = mergedColumnConfig?.[id];
          const filterType = inferFilterType(colDef, config);
          let options = getConfiguredOptions(colDef, config);
          if (filterType === "set" && !options.length) {
            const rowValues: unknown[] = [];
            liveApiRef.current?.forEachNode?.((node) => {
              rowValues.push(
                liveApiRef.current?.getCellValue?.({
                  rowNode: node,
                  colKey: id
                }) ??
                  (colDef.field
                    ? (node.data as Record<string, unknown> | undefined)?.[
                        colDef.field
                      ]
                    : undefined)
              );
            });
            options = toStringOptions(rowValues);
          }
          return {
            id,
            label:
              liveApiRef.current?.getDisplayNameForColumn?.(column, "header") ||
              colDef.headerName ||
              id,
            filterType,
            options,
            selectable: colDef.suppressColumnsToolPanel !== true
          };
        })
        .filter(({ id, label }) => !!id && !!label)
        .sort((left, right) =>
          left.label.localeCompare(right.label, undefined, {
            sensitivity: "base"
          })
        ),
    [gridColumns, mergedColumnConfig]
  );

  useEffect(() => {
    if (rules !== undefined) {
      setFormattingColumns(normalizeConditionalFormattingRules(rules));
    }
  }, [rules]);

  useEffect(() => {
    const refreshColumns = (gridApi?: GridApi<TData>) => {
      const nextApi = gridApi ?? liveApiRef.current;
      if (!nextApi || nextApi.isDestroyed?.()) return;
      setGridColumns([...(nextApi.getAllGridColumns?.() ?? [])]);
    };
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<OpenConditionalFormattingDetail>)
        .detail;
      const eventApi = detail?.api as GridApi<TData> | undefined;
      const hostApi = liveApiRef.current;
      if (!eventApi || (hostApi && hostApi !== eventApi)) return;
      liveApiRef.current = hostApi ?? eventApi;
      const gridApi = liveApiRef.current;
      setFormattingColumns(readRules());
      setLiveColumnConfig(getLiveContext()?.conditionalFormattingColumnConfig);
      refreshColumns(gridApi);
      const initialColumnId = detail.columnId;
      if (!initialColumnId) {
        setPopup({});
        return;
      }
      const existing = readRules().find(
        (entry) => entry.columnId === initialColumnId
      );
      setPopup({ columnId: initialColumnId, value: existing });
    };

    window.addEventListener(OPEN_CONDITIONAL_FORMATTING_EVENT, onOpen);
    const gridApi = liveApiRef.current;
    if (gridApi && !gridApi.isDestroyed?.()) {
      gridApi.addEventListener("newColumnsLoaded", refreshColumns as any);
      gridApi.addEventListener("gridColumnsChanged", refreshColumns as any);
      gridApi.addEventListener(
        "displayedColumnsChanged",
        refreshColumns as any
      );
      refreshColumns(gridApi);
    }
    return () => {
      window.removeEventListener(OPEN_CONDITIONAL_FORMATTING_EVENT, onOpen);
      if (!gridApi || gridApi.isDestroyed?.()) return;
      gridApi.removeEventListener("newColumnsLoaded", refreshColumns as any);
      gridApi.removeEventListener("gridColumnsChanged", refreshColumns as any);
      gridApi.removeEventListener(
        "displayedColumnsChanged",
        refreshColumns as any
      );
    };
    // Grid context is intentionally read live when the popup opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, gridRef]);

  const publish = (next: ConditionalFormattingRule[]) => {
    if (cannotEditConditionalFormatting) return;
    setFormattingColumns(next);
    onRulesChange?.(next);
    (
      getLiveContext()?.onConditionalFormattingRulesChange ??
      context?.onConditionalFormattingRulesChange
    )?.(next);
    liveApiRef.current?.refreshCells?.({ force: true });
  };

  return (
    <SetupConditionalFormattingPopup
      open={!!popup}
      columns={columns}
      formattedColumnIds={formattingColumns.map(({ columnId }) => columnId)}
      value={popup?.value}
      initialColumnId={popup?.columnId}
      readOnly={cannotEditConditionalFormatting}
      onCancel={() => setPopup(null)}
      onClear={() => {
        if (cannotEditConditionalFormatting) return;
        const columnId = popup?.value?.columnId ?? popup?.columnId;
        if (columnId) {
          publish(
            formattingColumns.filter((item) => item.columnId !== columnId)
          );
          const columnHeader =
            columns.find((column) => column.id === columnId)?.label ?? columnId;
          message.success(
            `Conditional formatting cleared from ${columnHeader}`
          );
        }
        setPopup(null);
      }}
      onSave={(entry) => {
        if (cannotEditConditionalFormatting) return;
        publish([
          ...formattingColumns.filter(
            (item) => item.columnId !== entry.columnId
          ),
          entry
        ]);
        const columnHeader =
          columns.find((column) => column.id === entry.columnId)?.label ??
          entry.columnId;
        message.success(`Conditional formatting applied to ${columnHeader}`);
        setPopup(null);
      }}
    />
  );
}
